// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import useProductFeatureSection from 'src/hooks/use-product-feature-page';

// ----------------------------------------------------------------------

export default function ProductFeatureSection() {

  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const productFeaturePageData = useSelector((state) => state.website.productFeaturePage);
  const { fetchOnMount } = useProductFeatureSection();
  const { enqueueSnackbar } = useSnackbar();



  const [formData, setFormData] = useState({
    // Product Features Section
    productFeaturesSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: []
    },

    // Users Management Section
    usersManagementSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: []
    },

    // Administration & Control Section
    administrationSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Security & Privacy Section
    securitySection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Office Management Section
    officeManagementSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Pre-construction Section
    preConstructionSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Construction Section
    constructionSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: []
    }
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateFeature = (sectionName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: prev[sectionName].features.map((feature, i) =>
          i === index ? { ...feature, [field]: value } : feature
        )
      }
    }));
  };

  const addFeature = (sectionName) => {
    const newFeature = {
      id: `feature-${Date.now()}`,
      title: "",
      iconUrl: "",
      backgroundColor: "#ffffff",
      textColor: "#000000"
    };

    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: [...prev[sectionName].features, newFeature]
      }
    }));
  };

  const removeFeature = (sectionName, index) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: prev[sectionName].features.filter((_, i) => i !== index)
      }
    }));
  };


  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    if (productFeaturePageData?.product_feature_page) {
      // Create a deep copy to avoid mutating the read-only Redux store data
      const deepCopy = JSON.parse(JSON.stringify(productFeaturePageData.product_feature_page));
      setFormData(prev => ({
        ...prev,
        ...deepCopy
      }));
    }
  }, [productFeaturePageData])

  const handleSave = async () => {
    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.product_feature_page.product_feature_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(productFeaturePageData)),
            product_feature_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const renderSectionHeader = (title) => (
    <Stack direction="row" alignItems="center" sx={{ mb: 0 }}>
      <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
      <Typography variant="h6" color="primary">{title}</Typography>
    </Stack>
  );

  const renderFeatureGrid = (features, sectionName, title) => (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => addFeature(sectionName)}
        >
          Add Feature
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={feature.id || index}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.neutral', minHeight: 320 }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="primary">
                  Feature {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeFeature(sectionName, index)}
                  sx={{ minWidth: 25, p: 0.2 }}
                >
                  ×
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {/* <TextField
                  fullWidth
                  label="Feature ID"
                  size="small"
                  value={feature.id}
                  onChange={(e) => updateFeature(sectionName, index, 'id', e.target.value)}
                /> */}

                <TextField
                  fullWidth
                  label="Feature Title"
                  size="small"
                  value={feature.title}
                  onChange={(e) => updateFeature(sectionName, index, 'title', e.target.value)}
                  placeholder="Enter feature title"
                />

                <TextField
                  fullWidth
                  label="Icon URL"
                  size="small"
                  value={feature.iconUrl}
                  onChange={(e) => updateFeature(sectionName, index, 'iconUrl', e.target.value)}
                  placeholder="Enter icon URL"
                />

                <TextField
                  fullWidth
                  label="Background Color"
                  type="color"
                  size="small"
                  value={feature.backgroundColor}
                  onChange={(e) => updateFeature(sectionName, index, 'backgroundColor', e.target.value)}
                  sx={{ '& input': { height: 30 } }}
                />

                <TextField
                  fullWidth
                  label="Text Color"
                  type="color"
                  size="small"
                  value={feature.textColor}
                  onChange={(e) => updateFeature(sectionName, index, 'textColor', e.target.value)}
                  sx={{ '& input': { height: 30 } }}
                />

                {/* Icon Preview */}
                {feature.iconUrl && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 60,
                      bgcolor: feature.backgroundColor,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={feature.iconUrl}
                      alt={`${feature.title} Icon`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        display: 'none',
                        color: 'text.disabled',
                        fontSize: 10
                      }}
                    >
                      Invalid URL
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderSectionContent = (sectionKey, sectionData, sectionTitle) => (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Title"
            value={sectionData.title}
            onChange={(e) => updateFormData(`${sectionKey}.title`, e.target.value)}
            placeholder="Enter section title"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Title Color"
            type="color"
            value={sectionData.titleColor}
            onChange={(e) => updateFormData(`${sectionKey}.titleColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>

        {/* Subtitle */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Subtitle"
            value={sectionData.subtitle}
            onChange={(e) => updateFormData(`${sectionKey}.subtitle`, e.target.value)}
            placeholder="Enter section subtitle"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Subtitle Color"
            type="color"
            value={sectionData.subtitleColor}
            onChange={(e) => updateFormData(`${sectionKey}.subtitleColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Description"
            multiline
            rows={4}
            value={sectionData.description}
            onChange={(e) => updateFormData(`${sectionKey}.description`, e.target.value)}
            placeholder="Enter section description"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Description Color"
            type="color"
            value={sectionData.descriptionColor}
            onChange={(e) => updateFormData(`${sectionKey}.descriptionColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>
      </Grid>

      {/* Features */}
      {sectionData.features && sectionData.features.length > 0 && (
        renderFeatureGrid(sectionData.features, sectionKey, `${sectionTitle} Features`)
      )}
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Typography variant="h4">
            Product Feature
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              const allExpanded = Object.keys(formData).reduce((acc, key) => {
                acc[key] = true;
                return acc;
              }, {});
              setExpandedSections(allExpanded);
            }}
          >
            Expand All
          </Button>
        </Stack>

        <Stack spacing={1}>
          {/* Product Features Section */}
          <Accordion
            expanded={expandedSections.productFeaturesSection || false}
            onChange={handleAccordionChange('productFeaturesSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Product Features Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('productFeaturesSection', formData.productFeaturesSection, 'Product Features')}
            </AccordionDetails>
          </Accordion>

          {/* Users Management Section */}
          <Accordion
            expanded={expandedSections.usersManagementSection || false}
            onChange={handleAccordionChange('usersManagementSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Users Management Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('usersManagementSection', formData.usersManagementSection, 'Users Management')}
            </AccordionDetails>
          </Accordion>

          {/* Administration & Control Section */}
          <Accordion
            expanded={expandedSections.administrationSection || false}
            onChange={handleAccordionChange('administrationSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Administration & Control Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('administrationSection', formData.administrationSection, 'Administration & Control')}
            </AccordionDetails>
          </Accordion>

          {/* Security & Privacy Section */}
          <Accordion
            expanded={expandedSections.securitySection || false}
            onChange={handleAccordionChange('securitySection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Security & Privacy Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('securitySection', formData.securitySection, 'Security & Privacy')}
            </AccordionDetails>
          </Accordion>

          {/* Office Management Section */}
          <Accordion
            expanded={expandedSections.officeManagementSection || false}
            onChange={handleAccordionChange('officeManagementSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Office Management Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('officeManagementSection', formData.officeManagementSection, 'Office Management')}
            </AccordionDetails>
          </Accordion>

          {/* Pre-construction Section */}
          <Accordion
            expanded={expandedSections.preConstructionSection || false}
            onChange={handleAccordionChange('preConstructionSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Pre-construction Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('preConstructionSection', formData.preConstructionSection, 'Pre-construction')}
            </AccordionDetails>
          </Accordion>

          {/* Construction Section */}
          <Accordion
            expanded={expandedSections.constructionSection || false}
            onChange={handleAccordionChange('constructionSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Construction Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('constructionSection', formData.constructionSection, 'Construction')}
            </AccordionDetails>
          </Accordion>
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 4, mt: 0, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
